import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  X,
  User,
  GraduationCap,
  FileText,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Check,
  CheckCircle,
  Phone,
  Mail,
  AlertTriangle,
  Award,
  Briefcase,
  Upload,
  Keyboard,
  Eye,
  EyeOff,
  Trash2,
  Loader2,
  CreditCard,
  BookOpen,
  Info,
  FileSpreadsheet,
  Copy,
  Printer,
  Download,
  Key,
  Gift,
  Plus,
  Clock,
  Calendar,
  ClipboardCheck,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { createStudentUser, uploadStudentDocument, fetchActiveCampuses, type CreateStudentUserDto, type StudentDocumentType as ServiceDocumentType, type CampusOption } from '@/services/userService';
import { fetchFilieresGrouped } from '@/services/filieresService';
import type { FiliereWithOptions } from '@/types/filiere.types';
import {
  fetchCountries,
  getCountriesSync,
  fetchSeriesBac,
  fetchProvinces,
  fetchDiplomes,
  type NomenclatureCountry,
  type NomenclatureSerieBac,
  type NomenclatureProvince,
  type NomenclatureDiplome,
} from '@/services/nomenclatureService';
import {
  fetchAcademicYears,
  fetchCurrentAcademicYear,
  generateAcademicYearOptions,
  getCurrentAcademicStartYear,
  generateAcademicYearName,
  type AcademicYear,
} from '@/services/academicPlanningService';
import { emergencyRelationSelectOptions } from '@/shared/constants/emergencyRelation';
// Document service imports (unused for now but kept for future document upload)
// import { uploadDocument as uploadDocumentToBackend, deleteDocument as deleteDocumentFromBackend, getDocumentPreviewUrl } from '@/services/documentsService';

// ==================== CONSTANTS ====================

const ARABIC_KEYBOARD_LAYOUT = [
  ['ض', 'ص', 'ث', 'ق', 'ف', 'غ', 'ع', 'ه', 'خ', 'ح', 'ج'],
  ['ش', 'س', 'ي', 'ب', 'ل', 'ا', 'ت', 'ن', 'م', 'ك', 'ط'],
  ['ئ', 'ء', 'ؤ', 'ر', 'لا', 'ى', 'ة', 'و', 'ز', 'ظ', 'د'],
];

const COUNTRY_PHONE_CODES = [
  { code: 'MA', name: 'Maroc', dialCode: '+212', flag: '🇲🇦' },
  { code: 'DZ', name: 'Algérie', dialCode: '+213', flag: '🇩🇿' },
  { code: 'TN', name: 'Tunisie', dialCode: '+216', flag: '🇹🇳' },
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷' },
  { code: 'ES', name: 'Espagne', dialCode: '+34', flag: '🇪🇸' },
  { code: 'BE', name: 'Belgique', dialCode: '+32', flag: '🇧🇪' },
  { code: 'SN', name: 'Sénégal', dialCode: '+221', flag: '🇸🇳' },
  { code: 'CI', name: "Côte d'Ivoire", dialCode: '+225', flag: '🇨🇮' },
];

// Emergency contact relationship options - matches StudentProfileForm
const FALLBACK_SERIES_BAC = [
  { code: 'SMA', label: 'Sciences Mathématiques A' },
  { code: 'SMB', label: 'Sciences Mathématiques B' },
  { code: 'SVT', label: 'Sciences de la Vie et de la Terre' },
  { code: 'SPC', label: 'Sciences Physiques et Chimiques' },
  { code: 'SGC', label: 'Sciences de Gestion Comptable' },
  { code: 'SE', label: 'Sciences Économiques' },
  { code: 'SH', label: 'Sciences Humaines' },
  { code: 'L', label: 'Lettres' },
  { code: 'LA', label: 'Lettres - Option Arabe' },
  { code: 'LF', label: 'Lettres - Option Français' },
  { code: 'STM', label: 'Sciences et Technologies Mécaniques' },
  { code: 'STE', label: 'Sciences et Technologies Électriques' },
  { code: 'AA', label: 'Arts Appliqués' },
  { code: 'AUTRE', label: 'Autre / Étranger' },
];

const FALLBACK_DIPLOMES = [
  { id: 'BAC', labelFr: 'Baccalauréat' },
  { id: 'DEUG', labelFr: 'DEUG' },
  { id: 'DEUST', labelFr: 'DEUST' },
  { id: 'DUT', labelFr: 'DUT' },
  { id: 'BTS', labelFr: 'BTS' },
  { id: 'LICENCE', labelFr: 'Licence' },
  { id: 'LP', labelFr: 'Licence Professionnelle' },
  { id: 'MASTER', labelFr: 'Master' },
  { id: 'INGENIEUR', labelFr: 'Diplôme d\'Ingénieur' },
  { id: 'DOCTORAT', labelFr: 'Doctorat' },
  { id: 'AUTRE', labelFr: 'Autre' },
];

const FALLBACK_PROVINCES = [
  'Agadir-Ida-Ou-Tanane',
  'Al Haouz',
  'Al Hoceima',
  'Azilal',
  'Béni Mellal',
  'Ben Slimane',
  'Berkane',
  'Berrechid',
  'Boulemane',
  'Casablanca',
  'Chefchaouen',
  'Chichaoua',
  'Chtouka-Aït Baha',
  'Driouch',
  'El Hajeb',
  'El Jadida',
  'El Kelâa des Sraghna',
  'Errachidia',
  'Essaouira',
  'Fahs-Anjra',
  'Fès',
  'Figuig',
  'Fquih Ben Salah',
  'Guelmim',
  'Guercif',
  'Ifrane',
  'Inezgane-Aït Melloul',
  'Jerada',
  'Kénitra',
  'Khémisset',
  'Khénifra',
  'Khouribga',
  'Laâyoune',
  'Larache',
  'Marrakech',
  'Médiouna',
  'Meknès',
  'Midelt',
  'Mohammedia',
  'Moulay Yacoub',
  'Nador',
  'Nouaceur',
  'Ouarzazate',
  'Ouazzane',
  'Oued Ed-Dahab',
  'Oujda-Angad',
  'Rabat',
  'Rehamna',
  'Safi',
  'Salé',
  'Sefrou',
  'Settat',
  'Sidi Bennour',
  'Sidi Ifni',
  'Sidi Kacem',
  'Sidi Slimane',
  'Skhirate-Témara',
  'Tan-Tan',
  'Tanger-Assilah',
  'Taounate',
  'Taourirt',
  'Tarfaya',
  'Taroudannt',
  'Tata',
  'Taza',
  'Tétouan',
  'Tinghir',
  'Tiznit',
  'Youssoufia',
  'Zagora',
  'Province étrangère',
];

// Options for Oui/Non selects
const OUI_NON_OPTIONS = [
  { value: 'N', label: 'Non' },
  { value: 'O', label: 'Oui' },
];

// Mention options
const MENTION_OPTIONS = [
  { value: 'ns', label: 'Non spécifié' },
  { value: 'p', label: 'Passable' },
  { value: 'ab', label: 'Assez Bien' },
  { value: 'b', label: 'Bien' },
  { value: 'tb', label: 'Très Bien' },
  { value: 'e', label: 'Excellent' },
];

// Handicap options - local fallback
const HANDICAP_OPTIONS = [
  { value: 'N', label: 'Aucun handicap' },
  { value: 'A', label: 'Handicap auditif' },
  { value: 'M', label: 'Handicap moteur' },
  { value: 'V', label: 'Handicap visuel' },
  { value: 'AM', label: 'Auditif + Moteur' },
  { value: 'AV', label: 'Auditif + Visuel' },
  { value: 'MV', label: 'Moteur + Visuel' },
  { value: 'T', label: 'Handicap total' },
  { value: 'XX', label: 'Autre handicap' },
];

// Situation options
const SITUATION_OPTIONS = [
  { value: 'N', label: 'Nouveau inscrit' },
  { value: 'R', label: 'Redoublant' },
  { value: 'P', label: 'Promu' },
];

// ==================== TYPES ====================

interface CreateStudentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onBulkImport?: () => void;
}

type Section = 'basic' | 'coordinates' | 'emergency' | 'academic' | 'financial' | 'diploma' | 'status' | 'documents' | 'physicalDocs';

type StudentDocumentType =
  | 'CIN_FRONT'
  | 'CIN_BACK'
  | 'PASSPORT'
  | 'DIPLOME_BAC_SCAN'
  | 'RELEVES_NOTES_BAC'
  | 'DIPLOME_ACCES_SCAN'
  | 'RELEVES_NOTES'
  | 'CV'
  | 'LETTRE_MOTIVATION'
  | 'PHOTO';

interface UploadedDocument {
  type: StudentDocumentType;
  file?: File;
  previewUrl: string;
  backendId?: string;
  isStoredInBackend?: boolean;
  isUploading?: boolean;
}

// ==================== HELPER FUNCTIONS ====================

// Capitalize first letter of each word (for first names)
function capitalizeFirstLetter(value: string): string {
  return value
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function generateAnneesUniversitaires(count: number = 20, includeOld: boolean = true): string[] {
  const currentYear = new Date().getFullYear();
  const annees: string[] = [];
  const startYear = includeOld ? currentYear - count + 1 : currentYear;
  const endYear = currentYear + 1;

  for (let year = endYear; year >= startYear; year--) {
    annees.push(`${year}/${year + 1}`);
  }
  return annees;
}

function getAnneeUniversitaireActuelle(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  // Academic year starts in September
  if (month >= 8) {
    return `${year}/${year + 1}`;
  }
  return `${year - 1}/${year}`;
}

// ==================== SUB-COMPONENTS ====================

// Virtual Arabic Keyboard
const VirtualArabicKeyboard: React.FC<{
  onKeyPress: (char: string) => void;
  onBackspace: () => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLDivElement>;
}> = ({ onKeyPress, onBackspace, onClose, anchorRef }) => {
  const [position, setPosition] = useState({ top: 0, left: 0, width: 300 });

  useEffect(() => {
    const updatePosition = () => {
      if (anchorRef.current) {
        const rect = anchorRef.current.getBoundingClientRect();
        const keyboardHeight = 180;
        const viewportHeight = window.innerHeight;
        const showAbove = rect.bottom + keyboardHeight > viewportHeight && rect.top > keyboardHeight;

        setPosition({
          top: showAbove ? rect.top - keyboardHeight - 5 : rect.bottom + 5,
          left: rect.left,
          width: Math.max(rect.width, 280),
        });
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [anchorRef]);

  return ReactDOM.createPortal(
    <div
      data-arabic-keyboard
      className="fixed z-[9999] p-2 bg-white border border-gray-300 rounded-lg shadow-xl"
      style={{ top: position.top, left: position.left, width: position.width }}
    >
      <div className="flex justify-between items-center mb-2 pb-2 border-b border-gray-200">
        <span className="text-xs text-gray-500 font-medium">لوحة المفاتيح العربية</span>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="space-y-1">
        {ARABIC_KEYBOARD_LAYOUT.map((row, rowIndex) => (
          <div key={rowIndex} className="flex justify-center gap-0.5">
            {row.map((char, charIndex) => (
              <button
                key={charIndex}
                type="button"
                onClick={() => onKeyPress(char)}
                className="w-7 h-8 text-sm font-medium bg-gray-100 hover:bg-primary-100 hover:text-primary-700 rounded transition-colors border border-gray-200"
              >
                {char}
              </button>
            ))}
          </div>
        ))}
        <div className="flex justify-center gap-1 mt-2">
          <button
            type="button"
            onClick={() => onKeyPress(' ')}
            className="flex-1 h-8 text-xs bg-gray-100 hover:bg-gray-200 rounded border border-gray-200"
          >
            espace
          </button>
          <button
            type="button"
            onClick={onBackspace}
            className="w-16 h-8 text-xs bg-red-50 hover:bg-red-100 text-red-600 rounded border border-red-200"
          >
            ← effacer
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Arabic Input Component
const ArabicInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  error?: boolean;
}> = ({ value, onChange, placeholder, error }) => {
  const [showKeyboard, setShowKeyboard] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const keyboardElement = document.querySelector('[data-arabic-keyboard]');
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node) &&
        (!keyboardElement || !keyboardElement.contains(e.target as Node))
      ) {
        setShowKeyboard(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyPress = (char: string) => {
    const input = inputRef.current;
    if (input) {
      const start = input.selectionStart || value.length;
      const end = input.selectionEnd || value.length;
      const newValue = value.slice(0, start) + char + value.slice(end);
      onChange(newValue);
      setTimeout(() => {
        const newPos = start + char.length;
        input.setSelectionRange(newPos, newPos);
        input.focus();
      }, 0);
    } else {
      onChange(value + char);
    }
  };

  const handleBackspace = () => {
    const input = inputRef.current;
    if (input && value.length > 0) {
      const start = input.selectionStart || value.length;
      const end = input.selectionEnd || value.length;
      if (start === end && start > 0) {
        const newValue = value.slice(0, start - 1) + value.slice(end);
        onChange(newValue);
      } else if (start !== end) {
        const newValue = value.slice(0, start) + value.slice(end);
        onChange(newValue);
      }
    } else if (value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        dir="rtl"
        className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-right ${
          error ? 'border-red-300' : 'border-gray-300'
        }`}
        placeholder={placeholder}
      />
      <button
        type="button"
        onClick={() => {
          setShowKeyboard(!showKeyboard);
          if (!showKeyboard) inputRef.current?.focus();
        }}
        className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-colors ${
          showKeyboard ? 'bg-primary-100 text-primary-600' : 'text-gray-400 hover:text-gray-600'
        }`}
        title={showKeyboard ? 'Fermer le clavier' : 'Ouvrir le clavier arabe'}
      >
        <Keyboard className="w-4 h-4" />
      </button>

      {showKeyboard && (
        <VirtualArabicKeyboard
          onKeyPress={handleKeyPress}
          onBackspace={handleBackspace}
          onClose={() => setShowKeyboard(false)}
          anchorRef={containerRef}
        />
      )}
    </div>
  );
};

// Phone Country Selector
const PhoneCountrySelector: React.FC<{
  value: string;
  onChange: (code: string) => void;
}> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selected = COUNTRY_PHONE_CODES.find((c) => c.code === value) || COUNTRY_PHONE_CODES[0];

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 px-2 py-2 border border-gray-300 rounded-l-lg bg-gray-50 hover:bg-gray-100 min-w-[90px]"
      >
        <span className="text-lg">{selected.flag}</span>
        <span className="text-sm text-gray-600">{selected.dialCode}</span>
        <ChevronDown className="w-3 h-3 text-gray-400" />
      </button>

      {isOpen && (
        <div className="absolute z-50 top-full left-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {COUNTRY_PHONE_CODES.map((country) => (
            <button
              key={country.code}
              type="button"
              onClick={() => {
                onChange(country.code);
                setIsOpen(false);
              }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 ${
                value === country.code ? 'bg-primary-50' : ''
              }`}
            >
              <span className="text-lg">{country.flag}</span>
              <span className="flex-1 text-sm">{country.name}</span>
              <span className="text-xs text-gray-500">{country.dialCode}</span>
              {value === country.code && <Check className="w-4 h-4 text-primary-600" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Filterable Dropdown Component - Type to filter naturally without separate search box
// Uses React Portal to render dropdown outside of overflow:hidden containers
interface FilterableDropdownOption {
  value: string;
  label: string;
  sublabel?: string;
}

const FilterableDropdown: React.FC<{
  value: string;
  onChange: (value: string) => void;
  options: FilterableDropdownOption[];
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
}> = ({ value, onChange, options, placeholder = 'Sélectionner...', disabled = false, error = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selected = options.find(o => o.value === value);

  // Filter options based on input when typing
  const filtered = isTyping && inputValue
    ? options.filter(o =>
        o.label.toLowerCase().includes(inputValue.toLowerCase()) ||
        (o.sublabel && o.sublabel.toLowerCase().includes(inputValue.toLowerCase()))
      )
    : options;

  // Update dropdown position when opening and on scroll/resize
  useEffect(() => {
    if (!isOpen || !containerRef.current) return;

    const updatePosition = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width,
        });
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  // Handle click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const isOutsideContainer = containerRef.current && !containerRef.current.contains(target);
      const isOutsideDropdown = !dropdownRef.current || !dropdownRef.current.contains(target);

      if (isOutsideContainer && isOutsideDropdown) {
        setIsOpen(false);
        setInputValue('');
        setIsTyping(false);
      }
    };

    // Small delay to avoid immediate close on open click
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setIsTyping(true);
    if (!isOpen) setIsOpen(true);
  };

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setInputValue('');
    setIsTyping(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setInputValue('');
      setIsTyping(false);
    }
    if (e.key === 'Enter' && filtered.length === 1) {
      handleSelect(filtered[0].value);
    }
  };

  const displayValue = isTyping ? inputValue : (selected?.label || '');

  // Dropdown menu rendered via portal
  const dropdownMenu = isOpen ? ReactDOM.createPortal(
    <div
      ref={dropdownRef}
      className="fixed bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto"
      style={{
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
        zIndex: 9999,
      }}
    >
      {filtered.length === 0 ? (
        <div className="px-4 py-3 text-sm text-gray-500 text-center">Aucun résultat</div>
      ) : (
        filtered.map(option => (
          <button
            key={option.value}
            type="button"
            onClick={() => handleSelect(option.value)}
            className={`w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-gray-50 transition-colors ${
              value === option.value ? 'bg-primary-50' : ''
            }`}
          >
            <div className="flex-1 min-w-0">
              <div className="text-sm text-gray-900 truncate">{option.label}</div>
              {option.sublabel && (
                <div className="text-xs text-gray-500 truncate">{option.sublabel}</div>
              )}
            </div>
            {value === option.value && <Check className="w-4 h-4 text-primary-600 flex-shrink-0 ml-2" />}
          </button>
        ))
      )}
    </div>,
    document.body
  ) : null;

  return (
    <div className="relative" ref={containerRef}>
      <div
        className={`w-full flex items-center border rounded-lg transition-colors ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
        } ${error ? 'border-red-300' : 'border-gray-300'} ${isOpen ? 'ring-2 ring-primary-500 border-primary-500' : ''}`}
      >
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full px-4 py-2 bg-transparent rounded-lg focus:outline-none ${
            disabled ? 'cursor-not-allowed text-gray-500' : 'text-gray-900'
          } ${!displayValue ? 'text-gray-500' : ''}`}
        />
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className="px-2 py-2"
        >
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {dropdownMenu}
    </div>
  );
};

// Document Row Component
const DocumentRow: React.FC<{
  label: string;
  required: boolean;
  document?: UploadedDocument;
  onUpload: (file: File) => void;
  onRemove: () => void;
  onPreview: () => void;
}> = ({ label, required, document, onUpload, onRemove, onPreview }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onUpload(file);
    if (inputRef.current) inputRef.current.value = '';
  };

  const isUploading = document?.isUploading;
  const isStored = document?.isStoredInBackend;

  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </span>
      </div>

      <div className="flex items-center gap-2">
        {isUploading ? (
          <div className="flex items-center gap-2 px-3 py-1.5 text-sm text-amber-600 bg-amber-50 rounded">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Envoi...</span>
          </div>
        ) : document ? (
          <>
            <span className={`text-xs px-2 py-1 rounded ${isStored ? 'text-green-600 bg-green-50' : 'text-amber-600 bg-amber-50'}`}>
              {isStored ? 'OK' : 'En attente'}
            </span>
            <button type="button" onClick={onPreview} className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded" title="Voir">
              <Eye className="w-4 h-4" />
            </button>
            <button type="button" onClick={onRemove} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded" title="Supprimer">
              <Trash2 className="w-4 h-4" />
            </button>
          </>
        ) : (
          <label className="flex items-center gap-2 px-3 py-1.5 text-sm text-primary-600 bg-primary-50 hover:bg-primary-100 rounded transition-colors cursor-pointer">
            <Upload className="w-4 h-4" />
            <span>Télécharger</span>
            <input ref={inputRef} type="file" accept="image/*,.pdf" onChange={handleFileChange} className="hidden" />
          </label>
        )}
      </div>
    </div>
  );
};

// Section Header Component
const SectionHeader: React.FC<{
  title: string;
  icon: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  required?: boolean;
  optional?: boolean;
}> = ({ title, icon, expanded, onToggle, required, optional }) => (
  <button
    type="button"
    onClick={onToggle}
    className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
  >
    <div className="flex items-center gap-2">
      {icon}
      <span className="font-medium text-gray-900">{title}</span>
      {required && <span className="text-xs text-red-500">*Obligatoire</span>}
      {optional && <span className="text-xs text-gray-500">(Optionnel)</span>}
    </div>
    {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
  </button>
);

// ==================== MAIN COMPONENT ====================

export const CreateStudentModal: React.FC<CreateStudentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onBulkImport,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<Section>>(new Set(['basic', 'coordinates', 'academic']));
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Credentials state - for students created without email
  const [showCredentials, setShowCredentials] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState<{
    studentName: string;
    username: string;
    tempPassword: string;
  } | null>(null);
  const [copiedField, setCopiedField] = useState<'username' | 'password' | null>(null);

  // Field refs for scrolling to errors
  const fieldRefs = {
    nom: useRef<HTMLInputElement>(null),
    prenom: useRef<HTMLInputElement>(null),
    email: useRef<HTMLInputElement>(null),
    telephone: useRef<HTMLInputElement>(null),
    filiere: useRef<HTMLDivElement>(null),
    campus: useRef<HTMLDivElement>(null),
    handicap: useRef<HTMLDivElement>(null),
    resident: useRef<HTMLDivElement>(null),
    fonctionnaire: useRef<HTMLDivElement>(null),
    pourcentageBourse: useRef<HTMLDivElement>(null),
    mobilite: useRef<HTMLDivElement>(null),
  };

  // Map fields to their sections
  const fieldToSection: Record<string, Section> = {
    nom: 'basic',
    prenom: 'basic',
    handicap: 'basic',
    email: 'coordinates',
    telephone: 'coordinates',
    filiere: 'academic',
    campus: 'academic',
    resident: 'status',
    fonctionnaire: 'status',
    pourcentageBourse: 'status',
    mobilite: 'status',
  };

  // Scroll to field with error
  const scrollToFieldError = (fieldName: keyof typeof fieldRefs) => {
    const section = fieldToSection[fieldName];

    // Expand the section if collapsed
    if (section && !expandedSections.has(section)) {
      setExpandedSections(prev => new Set([...prev, section]));
    }

    // Wait for section to expand, then scroll to field
    setTimeout(() => {
      const ref = fieldRefs[fieldName];
      if (ref?.current) {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Try to focus the input
        if ('focus' in ref.current && typeof ref.current.focus === 'function') {
          ref.current.focus();
        }
      }
    }, 100);
  };

  // Nomenclature data
  const [filieresGrouped, setFilieresGrouped] = useState<FiliereWithOptions[]>([]);
  const [countries, setCountries] = useState<NomenclatureCountry[]>(() => getCountriesSync());
  const [seriesBac, setSeriesBac] = useState<NomenclatureSerieBac[]>([]);
  const [provinces, setProvinces] = useState<NomenclatureProvince[]>([]);
  const [diplomes, setDiplomes] = useState<NomenclatureDiplome[]>([]);

  // Campus data
  const [campuses, setCampuses] = useState<CampusOption[]>([]);
  const [requiresCampusSelection, setRequiresCampusSelection] = useState(false);
  const [campusId, setCampusId] = useState('');

  // Academic year data
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([]);
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState<string>('');

  // Form state - Required fields (French nomenclature)
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [telephone, setTelephone] = useState('');
  const [phoneCountry, setPhoneCountry] = useState('MA');
  const [codeFiliereInscrit, setCodeFiliereInscrit] = useState('');
  const [niveauFormation, setNiveauFormation] = useState<number>(1);
  const [includeInEdep, setIncludeInEdep] = useState(true);

  // Identity fields (French nomenclature)
  const [prenomArabe, setPrenomArabe] = useState('');
  const [nomArabe, setNomArabe] = useState('');
  const [sexe, setSexe] = useState<'M' | 'F' | ''>('');
  const [dateNaissance, setDateNaissance] = useState('');
  const [paysOrigine, setPaysOrigine] = useState('MA');
  const [cnie, setCnie] = useState('');
  const [passport, setPassport] = useState('');
  const [handicap, setHandicap] = useState('');

  // Contact fields (French nomenclature)
  const [adresse, setAdresse] = useState('');
  const [ville, setVille] = useState('');
  const [codePostal, setCodePostal] = useState('');

  // Emergency contact (French nomenclature)
  const [urgenceNom, setUrgenceNom] = useState('');
  const [urgenceNumero, setUrgenceNumero] = useState('');
  const [urgenceAdresse, setUrgenceAdresse] = useState('');
  const [urgencePhoneCountry, setUrgencePhoneCountry] = useState('MA');
  const [urgenceRelation, setUrgenceRelation] = useState('');

  // Academic fields
  const [numeroMassarCNE, setNumeroMassarCNE] = useState('');
  const [situation, setSituation] = useState<'N' | 'R' | 'P'>('N');
  const [premiereAnneeInscription, setPremiereAnneeInscription] = useState(getAnneeUniversitaireActuelle());

  // Diploma fields
  const [serieBaccalaureat, setSerieBaccalaureat] = useState('');
  const [anneeObtentionBac, setAnneeObtentionBac] = useState('');
  const [diplomeAcces, setDiplomeAcces] = useState('Baccalauréat');
  const [specialiteDiplomeAcces, setSpecialiteDiplomeAcces] = useState('');
  const [mentionDiplomeAcces, setMentionDiplomeAcces] = useState<'p' | 'ab' | 'b' | 'tb' | 'e' | 'ns'>('ns');
  const [lieuObtentionDiplomeAcces, setLieuObtentionDiplomeAcces] = useState('');

  // Status fields
  const [resident, setResident] = useState<'O' | 'N'>('N');
  const [fonctionnaire, setFonctionnaire] = useState<'O' | 'N'>('N');
  const [pourcentageBourse, setPourcentageBourse] = useState(0);
  const [mobilite, setMobilite] = useState<'O' | 'N'>('N');

  // Type de Formation state
  const [typeFormation, setTypeFormation] = useState<'INITIALE' | 'CONTINUE'>('INITIALE');

  // Tuition preview state
  const [tuitionPreview, setTuitionPreview] = useState<{
    tuitionAmount: number;
    registrationFee: number;
    total: number;
    isConfigured: boolean;
    message?: string;
    availableAncillaryFees?: Array<{
      id: string;
      assignmentId: string;
      name: string;
      code: string;
      amount: number;
      description: string | null;
      scope: string;
    }>;
    availableDiscounts?: Array<{
      id: string;
      assignmentId: string;
      name: string;
      code: string;
      type: string;
      percentageValue: number | null;
      fixedAmount: number | null;
      description: string | null;
      requiresApproval: boolean;
      scope: string;
    }>;
  } | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_loadingTuitionPreview, setLoadingTuitionPreview] = useState(false);

  // All available ancillary fees and discounts for the tenant (loaded once)
  const [allAncillaryFees, setAllAncillaryFees] = useState<Array<{
    id: string;
    name: string;
    code: string;
    amount: number;
    description: string | null;
    isActive: boolean;
  }>>([]);
  const [allDiscounts, setAllDiscounts] = useState<Array<{
    id: string;
    name: string;
    code: string;
    type: string;
    percentageValue: number | null;
    fixedAmount: number | null;
    maxDeductionAmount: number | null;
    description: string | null;
    requiresApproval: boolean;
    isActive: boolean;
  }>>([]);
  const [allPaymentSchedules, setAllPaymentSchedules] = useState<Array<{
    id: string;
    name: string;
    code: string;
    description: string | null;
    type: string;
    isDefault: boolean;
    isActive: boolean;
    gracePeriodDays: number;
    installments: Array<{
      sequenceNumber: number;
      label: string;
      percentageOfTotal: number;
      dueDayOfMonth: number | null;
      dueMonth: number | null;
      relativeDays: number | null;
    }>;
  }>>([]);
  const [loadingFinancialOptions, setLoadingFinancialOptions] = useState(false);

  // Selected ancillary fees, discounts, and payment schedule
  const [selectedAncillaryFeeIds, setSelectedAncillaryFeeIds] = useState<string[]>([]);
  const [selectedDiscountIds, setSelectedDiscountIds] = useState<string[]>([]);
  const [selectedPaymentScheduleId, setSelectedPaymentScheduleId] = useState<string>('');

  // Physical document tracking for students
  const [physicalDocStudentCin, setPhysicalDocStudentCin] = useState(false);
  const [physicalDocStudentBac, setPhysicalDocStudentBac] = useState(false);
  const [physicalDocStudentReleveBac, setPhysicalDocStudentReleveBac] = useState(false);
  const [physicalDocStudentDernierDiplome, setPhysicalDocStudentDernierDiplome] = useState(false);
  const [physicalDocStudentReleveDiplome, setPhysicalDocStudentReleveDiplome] = useState(false);
  const [physicalDocStudentReleveAnneesPrecedentes, setPhysicalDocStudentReleveAnneesPrecedentes] = useState(false);
  const [physicalDocStudentCv, setPhysicalDocStudentCv] = useState(false);
  const [physicalDocStudentLettreMotivation, setPhysicalDocStudentLettreMotivation] = useState(false);

  // Calculate selected ancillary fees total
  const selectedAncillaryFeesTotal = allAncillaryFees
    .filter(f => selectedAncillaryFeeIds.includes(f.id))
    .reduce((sum, f) => sum + f.amount, 0);

  // Calculate selected discounts total (for display - actual discount requires validation)
  const selectedDiscountsInfo = allDiscounts
    .filter(d => selectedDiscountIds.includes(d.id))
    .map(d => {
      const baseTotal = (tuitionPreview?.total || 0) + selectedAncillaryFeesTotal;
      let amount = 0;
      if (d.percentageValue !== null) {
        amount = (baseTotal * d.percentageValue) / 100;
        // Apply max deduction cap if set
        if (d.maxDeductionAmount !== null && d.maxDeductionAmount > 0) {
          amount = Math.min(amount, d.maxDeductionAmount);
        }
      } else if (d.fixedAmount !== null) {
        amount = d.fixedAmount;
      }
      return { ...d, calculatedAmount: amount };
    });

  const selectedDiscountsTotal = selectedDiscountsInfo.reduce((sum, d) => sum + d.calculatedAmount, 0);

  // Final total (discounts shown but marked as pending)
  const finalTotal = (tuitionPreview?.total || 0) + selectedAncillaryFeesTotal;

  // Documents
  const [documents, setDocuments] = useState<UploadedDocument[]>([]);
  const [idDocumentType, setIdDocumentType] = useState<'CIN' | 'PASSPORT'>('CIN');
  const [previewDocument, setPreviewDocument] = useState<UploadedDocument | null>(null);

  // Get all filières for dropdown (user selects filière first, then niveau is limited based on filière)
  const allFilieres = filieresGrouped.map((f) => ({
    id: f.id,
    codeFiliere: f.codeFiliere,
    filiere: f.filiere,
    diplome: f.diplome,
    nombreAnnees: f.nombreAnnees,
    isOption: false,
    optionName: null,
    parentCodeFiliere: null,
  }));

  // Find selected filière from the list
  const selectedFiliere = allFilieres.find((f) => f.codeFiliere === codeFiliereInscrit);

  // Get available niveaux based on selected filière's nombreAnnees
  const availableNiveaux = selectedFiliere
    ? Array.from({ length: selectedFiliere.nombreAnnees }, (_, i) => i + 1)
    : [];

  // The actual code to use is directly the selected code (options are now in the main dropdown)
  const effectiveCodeFiliereInscrit = codeFiliereInscrit;
  const anneesUniversitaires = generateAnneesUniversitaires(20, true);
  // Check if diploma is Baccalauréat (case-insensitive, handles variations)
  const isBacDiplome = !diplomeAcces ||
    diplomeAcces.toLowerCase().includes('baccalauréat') ||
    diplomeAcces.toLowerCase().includes('baccalaureat') ||
    diplomeAcces.toUpperCase() === 'BAC';

  // Check if profile is complete enough to auto-validate
  // Required fields for auto-validation:
  // - Basic: nom, prenom, sexe, dateNaissance, paysOrigine
  // - Identity document: CIN (if Moroccan) or Passport (if foreign)
  // - Contact: telephone
  // - Academic: codeFiliereInscrit, niveauFormation
  // - Documents: At least identity document uploaded
  const isProfileComplete = (): boolean => {
    // Basic required fields
    if (!nom || !prenom || !sexe || !dateNaissance || !paysOrigine) return false;
    if (!telephone || !codeFiliereInscrit) return false;

    // Identity document based on nationality
    const isMoroccan = paysOrigine === 'MA';
    if (isMoroccan && !cnie) return false;
    if (!isMoroccan && !passport) return false;

    // Check campus if required
    if (requiresCampusSelection && !campusId) return false;

    // Check for identity document upload
    const hasIdentityDoc = isMoroccan
      ? (getDocument('CIN_FRONT') || getDocument('CIN_BACK'))
      : getDocument('PASSPORT');
    if (!hasIdentityDoc) return false;

    return true;
  };

  const profileComplete = isProfileComplete();

  // Fetch tuition preview
  const fetchTuitionPreview = useCallback(async () => {
    if (!codeFiliereInscrit || !niveauFormation) {
      setTuitionPreview(null);
      setSelectedAncillaryFeeIds([]);
      setSelectedDiscountIds([]);
      return;
    }

    // Find the filière UUID from the code
    const filiereForTuition = filieresGrouped.find((f) => f.codeFiliere === codeFiliereInscrit);
    if (!filiereForTuition) {
      setTuitionPreview(null);
      setSelectedAncillaryFeeIds([]);
      setSelectedDiscountIds([]);
      return;
    }

    setLoadingTuitionPreview(true);
    try {
      const params = new URLSearchParams({
        filiereId: filiereForTuition.id, // Use UUID instead of codeFiliere
        niveau: niveauFormation.toString(),
        typeFormation: typeFormation,
      });

      // Add campus if selected
      if (campusId) {
        params.append('campusId', campusId);
      }

      // Add academic year if selected
      if (selectedAcademicYearId) {
        params.append('academicYearId', selectedAcademicYearId);
      }

      const response = await fetch(`/api/financial/tuition/preview?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('nexedu_access_token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTuitionPreview(data);
        // Reset selections when preview changes
        setSelectedAncillaryFeeIds([]);
        setSelectedDiscountIds([]);
      } else {
        setTuitionPreview(null);
      }
    } catch (error) {
      console.error('Error fetching tuition preview:', error);
      setTuitionPreview(null);
    } finally {
      setLoadingTuitionPreview(false);
    }
  }, [codeFiliereInscrit, niveauFormation, campusId, typeFormation, filieresGrouped, selectedAcademicYearId]);

  // Fetch tuition preview when relevant fields change
  useEffect(() => {
    fetchTuitionPreview();
  }, [fetchTuitionPreview]);

  // Load nomenclatures and financial options
  useEffect(() => {
    if (isOpen) {
      loadNomenclatures();
      loadFinancialOptions();
    }
  }, [isOpen]);

  const loadNomenclatures = async () => {
    try {
      // Get auth token for academic years API
      const token = localStorage.getItem('nexedu_access_token') || '';

      const [filieresGroupedData, countriesData, seriesBacData, provincesData, diplomesData, campusesData, academicYearsData, currentAcademicYear] = await Promise.all([
        fetchFilieresGrouped(true).catch(() => []),
        fetchCountries().catch(() => []),
        fetchSeriesBac().catch(() => []),
        fetchProvinces().catch(() => []),
        fetchDiplomes().catch(() => []),
        fetchActiveCampuses().catch(() => ({ campuses: [], count: 0, requiresCampusSelection: false })),
        fetchAcademicYears(token).catch(() => []),
        fetchCurrentAcademicYear(token).catch(() => null),
      ]);

      // Use API data if available, otherwise keep defaults/fallbacks
      if (filieresGroupedData?.length) setFilieresGrouped(filieresGroupedData);
      if (countriesData?.length) setCountries(countriesData);
      if (seriesBacData?.length) setSeriesBac(seriesBacData);
      if (provincesData?.length) setProvinces(provincesData);
      if (diplomesData?.length) setDiplomes(diplomesData);

      // Generate academic year options: 2 years before and 2 years after current year
      const generatedYearOptions = generateAcademicYearOptions(2, 2);
      const currentYearName = generateAcademicYearName(getCurrentAcademicStartYear());

      // Create combined list of academic years
      // Use DB ID if available, otherwise use the year name as ID (for new years)
      const combinedYears: AcademicYear[] = generatedYearOptions.map(option => {
        const existingYear = academicYearsData?.find((y: AcademicYear) => y.name === option.value);
        if (existingYear) {
          return existingYear;
        }
        // Create a virtual year entry for years not in DB
        return {
          id: option.value, // Use name as ID for virtual years
          name: option.value,
          isCurrent: option.value === currentYearName,
          isActive: true,
          startDate: '',
          endDate: '',
          tenantId: '',
          createdAt: '',
          updatedAt: '',
        };
      });

      setAcademicYears(combinedYears);

      // Set default to current academic year (always based on today's date)
      // First priority: find the computed current year in combined list
      const currentYear = combinedYears.find(y => y.name === currentYearName);
      if (currentYear) {
        setSelectedAcademicYearId(currentYear.id);
      } else if (currentAcademicYear?.id) {
        // Fallback to backend's current academic year
        setSelectedAcademicYearId(currentAcademicYear.id);
      } else if (combinedYears.length > 0) {
        // Last fallback: first year in list
        setSelectedAcademicYearId(combinedYears[0].id);
      }

      // Set campus data
      if (campusesData?.campuses) {
        setCampuses(campusesData.campuses);
        setRequiresCampusSelection(campusesData.requiresCampusSelection);
        // If only one campus, auto-select it
        if (campusesData.campuses.length === 1) {
          setCampusId(campusesData.campuses[0].id);
        }
      }
    } catch (err) {
      console.error('Error loading nomenclatures:', err);
    }
  };

  // Load available ancillary fees, discounts, and payment schedules for the tenant
  const loadFinancialOptions = async () => {
    setLoadingFinancialOptions(true);
    try {
      const token = localStorage.getItem('nexedu_access_token') || '';
      const headers = { 'Authorization': `Bearer ${token}` };

      const [ancillaryFeesRes, discountsRes, schedulesRes] = await Promise.all([
        fetch('/api/financial/tuition/ancillary-fees?includeArchived=false', { headers }),
        fetch('/api/financial/tuition/discounts?includeArchived=false', { headers }),
        fetch('/api/financial/tuition/schedules?includeArchived=false', { headers }),
      ]);

      if (ancillaryFeesRes.ok) {
        const data = await ancillaryFeesRes.json();
        setAllAncillaryFees(data.fees || data.data || []);
      }

      if (discountsRes.ok) {
        const data = await discountsRes.json();
        setAllDiscounts(data.discounts || data.data || []);
      }

      if (schedulesRes.ok) {
        const data = await schedulesRes.json();
        const schedules = data.templates || data.data || [];
        setAllPaymentSchedules(schedules);
        // Auto-select default schedule if exists
        const defaultSchedule = schedules.find((s: any) => s.isDefault && s.isActive);
        if (defaultSchedule) {
          setSelectedPaymentScheduleId(defaultSchedule.id);
        }
      }
    } catch (err) {
      console.error('Error loading financial options:', err);
    } finally {
      setLoadingFinancialOptions(false);
    }
  };

  const toggleSection = (section: Section) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  // Document handling
  const handleUpload = useCallback(async (type: StudentDocumentType, file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Le fichier est trop grand (max 10MB)');
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setDocuments((prev) => {
      const filtered = prev.filter((d) => d.type !== type);
      return [...filtered, { type, file, previewUrl, isUploading: false }];
    });
  }, []);

  const handleRemove = useCallback((type: StudentDocumentType) => {
    setDocuments((prev) => {
      const docToRemove = prev.find((d) => d.type === type);
      if (docToRemove && docToRemove.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(docToRemove.previewUrl);
      }
      return prev.filter((d) => d.type !== type);
    });
  }, []);

  const getDocument = (type: StudentDocumentType) => documents.find((d) => d.type === type);

  // Format phone number for API
  const formatPhoneForApi = (phoneNum: string, countryCode: string): string => {
    const country = COUNTRY_PHONE_CODES.find((c) => c.code === countryCode);
    if (!country) return phoneNum;

    // If phone already starts with + or 0, return as is
    if (phoneNum.startsWith('+') || phoneNum.startsWith('0')) {
      return phoneNum;
    }

    // Otherwise, prepend dial code
    return country.dialCode + phoneNum;
  };

  const resetForm = () => {
    setPrenom('');
    setNom('');
    setEmail('');
    setTelephone('');
    setPhoneCountry('MA');
    setCodeFiliereInscrit('');
    setNiveauFormation(1);
    setIncludeInEdep(true);
    setPrenomArabe('');
    setNomArabe('');
    setSexe('');
    setDateNaissance('');
    setPaysOrigine('MA');
    setCnie('');
    setPassport('');
    setHandicap('');
    setAdresse('');
    setVille('');
    setCodePostal('');
    setUrgenceNom('');
    setUrgenceNumero('');
    setUrgenceAdresse('');
    setUrgencePhoneCountry('MA');
    setUrgenceRelation('');
    setNumeroMassarCNE('');
    setSituation('N');
    setPremiereAnneeInscription(getAnneeUniversitaireActuelle());
    setSerieBaccalaureat('');
    setAnneeObtentionBac('');
    setDiplomeAcces('Baccalauréat');
    setSpecialiteDiplomeAcces('');
    setMentionDiplomeAcces('ns');
    setLieuObtentionDiplomeAcces('');
    setResident('N');
    setFonctionnaire('N');
    setPourcentageBourse(0);
    setMobilite('N');
    setDocuments([]);
    setIdDocumentType('CIN');
    // Reset campus to auto-selected if only one campus
    setCampusId(campuses.length === 1 ? campuses[0].id : '');
    // Reset academic year to current (if available)
    const currentYear = academicYears.find((y) => y.isCurrent);
    setSelectedAcademicYearId(currentYear?.id || (academicYears.length > 0 ? academicYears[0].id : ''));
    setError(null);
    setFieldErrors({});
    setSuccess(false);
    setExpandedSections(new Set(['basic', 'academic']));
  };

  const handleClose = () => {
    resetForm();
    setShowCredentials(false);
    setCreatedCredentials(null);
    setCopiedField(null);
    onClose();
  };

  // Handle closing credentials modal and completing the flow
  const handleCloseCredentials = () => {
    setShowCredentials(false);
    setCreatedCredentials(null);
    setCopiedField(null);
    onSuccess();
    handleClose();
  };

  // Copy credential to clipboard
  const copyToClipboard = async (text: string, field: 'username' | 'password') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Print credentials
  const printCredentials = () => {
    if (!createdCredentials) return;
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Identifiants - ${createdCredentials.studentName}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 40px; }
              .header { text-align: center; margin-bottom: 30px; }
              .card { border: 1px solid #ccc; padding: 20px; border-radius: 8px; max-width: 400px; margin: 0 auto; }
              .field { margin-bottom: 15px; }
              .label { font-weight: bold; color: #666; font-size: 12px; }
              .value { font-size: 18px; font-family: monospace; background: #f5f5f5; padding: 8px; border-radius: 4px; }
              .footer { text-align: center; margin-top: 30px; color: #999; font-size: 12px; }
            </style>
          </head>
          <body>
            <div class="header">
              <h2>Identifiants de connexion</h2>
              <p>${createdCredentials.studentName}</p>
            </div>
            <div class="card">
              <div class="field">
                <div class="label">Identifiant</div>
                <div class="value">${createdCredentials.username}</div>
              </div>
              <div class="field">
                <div class="label">Mot de passe temporaire</div>
                <div class="value">${createdCredentials.tempPassword}</div>
              </div>
            </div>
            <div class="footer">
              <p>Ce mot de passe doit être changé lors de la première connexion.</p>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  // Download credentials as text file
  const downloadCredentials = () => {
    if (!createdCredentials) return;
    const content = `Identifiants de connexion - ${createdCredentials.studentName}
==========================================

Identifiant: ${createdCredentials.username}
Mot de passe temporaire: ${createdCredentials.tempPassword}

Note: Ce mot de passe doit être changé lors de la première connexion.
`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `identifiants_${createdCredentials.username}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async (e: React.FormEvent, autoValidate: boolean = false) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    setLoading(true);

    try {
      // Validate required fields one by one to scroll to the first error
      if (!nom) {
        setFieldErrors({ nom: 'Le nom est obligatoire' });
        scrollToFieldError('nom');
        throw new Error('Le nom est obligatoire');
      }
      if (!prenom) {
        setFieldErrors({ prenom: 'Le prénom est obligatoire' });
        scrollToFieldError('prenom');
        throw new Error('Le prénom est obligatoire');
      }
      // Email is now optional - students without email will use their student code as username
      if (!telephone) {
        setFieldErrors({ telephone: 'Le numéro de téléphone est obligatoire' });
        scrollToFieldError('telephone');
        throw new Error('Le numéro de téléphone est obligatoire');
      }
      if (!codeFiliereInscrit) {
        setFieldErrors({ filiere: 'La filière est obligatoire' });
        scrollToFieldError('filiere');
        throw new Error('La filière est obligatoire');
      }

      // Note: handicap and Section 6 (Status & Aides) fields are optional for staff creating students
      // Students filling their own profile will have these as required in StudentProfileForm

      // Validate campus selection if required
      if (requiresCampusSelection && !campusId) {
        setFieldErrors({ campus: 'Le campus est obligatoire' });
        scrollToFieldError('campus');
        throw new Error('Veuillez sélectionner un campus d\'affectation');
      }

      // Validate phone format
      // Since the country code is selected separately, we validate just the number part
      const cleanedPhone = telephone.replace(/^0+/, '').replace(/\s/g, '');

      if (phoneCountry === 'MA') {
        // Moroccan format: 9 digits starting with 5, 6, or 7 (without the +212 prefix)
        if (!/^[5-7]\d{8}$/.test(cleanedPhone)) {
          setFieldErrors({ telephone: 'Le numéro doit contenir 9 chiffres commençant par 5, 6 ou 7' });
          scrollToFieldError('telephone');
          throw new Error('Le numéro doit contenir 9 chiffres commençant par 5, 6 ou 7 (ex: 612345678)');
        }
      } else {
        // International format: must have at least 7 digits
        const digitsOnly = cleanedPhone.replace(/\D/g, '');
        if (digitsOnly.length < 7 || digitsOnly.length > 15) {
          setFieldErrors({ telephone: 'Le numéro doit contenir entre 7 et 15 chiffres' });
          scrollToFieldError('telephone');
          throw new Error('Le numéro de téléphone doit contenir entre 7 et 15 chiffres');
        }
      }

      // Format phone for API (add country code prefix)
      const formattedPhone = formatPhoneForApi(cleanedPhone, phoneCountry);

      // Validate email format only if email is provided
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setFieldErrors({ email: "Format d'email invalide" });
        scrollToFieldError('email');
        throw new Error("Format d'email invalide");
      }

      // Build the DTO with French nomenclature
      // Use effectiveCodeFiliereInscrit which is either the option code (for last year) or base code
      const data: CreateStudentUserDto = {
        prenom,
        nom,
        // Email is optional - only include if provided
        ...(email && { email }),
        telephone: formattedPhone,
        codeFiliereInscrit: effectiveCodeFiliereInscrit,
        niveauFormation,
        typeFormation,
        includeInEdep,
        // Optional identity fields (French nomenclature)
        ...(prenomArabe && { prenomArabe }),
        ...(nomArabe && { nomArabe }),
        ...(sexe && { sexe: sexe as 'M' | 'F' }),
        ...(dateNaissance && { dateNaissance }),
        ...(paysOrigine && { paysOrigine }),
        ...(cnie && { cnie: cnie.toUpperCase() }),
        ...(passport && { passport }),
        ...(handicap && { handicap }),
        ...(phoneCountry && { phoneCountry }),
        // Contact fields (French nomenclature)
        ...(adresse && { adresse }),
        ...(ville && { ville }),
        ...(codePostal && { codePostal }),
        // Academic fields
        ...(numeroMassarCNE && { numeroMassarCNE }),
        ...(situation && { situation }),
        ...(premiereAnneeInscription && { premiereAnneeInscription }),
        // Diploma fields
        ...(serieBaccalaureat && { serieBaccalaureat }),
        ...(anneeObtentionBac && { anneeObtentionBac }),
        ...(diplomeAcces && { diplomeAcces }),
        ...(!isBacDiplome && specialiteDiplomeAcces && { specialiteDiplomeAcces }),
        ...(mentionDiplomeAcces && { mentionDiplomeAcces }),
        ...(lieuObtentionDiplomeAcces && { lieuObtentionDiplomeAcces }),
        // Status fields
        ...(resident && { resident }),
        ...(fonctionnaire && { fonctionnaire }),
        ...(pourcentageBourse !== undefined && { pourcentageBourse }),
        ...(mobilite && { mobilite }),
        // Emergency contact (French nomenclature)
        ...(urgenceNom && { urgenceNom }),
        ...(urgenceNumero && {
          urgenceNumero: formatPhoneForApi(urgenceNumero, urgencePhoneCountry),
        }),
        ...(urgenceAdresse && { urgenceAdresse }),
        ...(urgenceRelation && { urgenceRelation }),
        ...(urgencePhoneCountry && { urgencePhoneCountry }),
        // Campus affiliation
        ...(campusId && { campusId }),
        // Academic year assignment
        ...(selectedAcademicYearId && { academicYearId: selectedAcademicYearId }),
        // Auto-validation flag
        ...(autoValidate && { autoValidate }),
        // Financial fields - selected ancillary fees, discounts, and payment schedule
        ...(selectedAncillaryFeeIds.length > 0 && { selectedAncillaryFeeIds }),
        ...(selectedDiscountIds.length > 0 && { selectedDiscountIds }),
        ...(selectedPaymentScheduleId && { selectedPaymentScheduleId }),
        // Physical document tracking
        physicalDocStudentCin,
        physicalDocStudentBac,
        physicalDocStudentReleveBac,
        physicalDocStudentDernierDiplome,
        physicalDocStudentReleveDiplome,
        physicalDocStudentReleveAnneesPrecedentes,
        physicalDocStudentCv,
        physicalDocStudentLettreMotivation,
      };

      const result = await createStudentUser(data);

      // Upload documents if any were selected
      const documentsToUpload = documents.filter((doc) => doc.file);
      if (documentsToUpload.length > 0 && result.student?.id) {
        // Mark documents as uploading
        setDocuments((prev) =>
          prev.map((doc) => (doc.file ? { ...doc, isUploading: true } : doc))
        );

        // Upload each document
        const uploadPromises = documentsToUpload.map(async (doc) => {
          try {
            await uploadStudentDocument(
              result.student.id,
              doc.type as ServiceDocumentType,
              doc.file!
            );
            // Mark as stored in backend
            setDocuments((prev) =>
              prev.map((d) =>
                d.type === doc.type
                  ? { ...d, isUploading: false, isStoredInBackend: true }
                  : d
              )
            );
          } catch (uploadError) {
            console.error(`Failed to upload document ${doc.type}:`, uploadError);
            // Mark upload as failed but don't fail the whole operation
            setDocuments((prev) =>
              prev.map((d) =>
                d.type === doc.type ? { ...d, isUploading: false } : d
              )
            );
          }
        });

        // Wait for all uploads to complete (but don't block on failures)
        await Promise.allSettled(uploadPromises);
      }

      setSuccess(true);

      // If student was created without email, show credentials modal
      if (result.credentials) {
        setCreatedCredentials({
          studentName: `${prenom} ${nom}`,
          username: result.credentials.username,
          tempPassword: result.credentials.tempPassword,
        });
        setShowCredentials(true);
        // Don't auto-close - let user copy/print credentials first
      } else {
        // Delay to show success message, then close
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 1500);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  // Get series bac list
  const seriesBacList =
    seriesBac.length > 0
      ? seriesBac.map((s) => ({ code: s.code, label: s.labelFr, type: s.type || 'Marocain' }))
      : FALLBACK_SERIES_BAC.map((s) => ({ ...s, type: 'Marocain' }));

  // Get diplomes list
  const diplomesList =
    diplomes.length > 0
      ? diplomes.map((d) => ({ id: d.id, label: d.labelFr }))
      : FALLBACK_DIPLOMES.map((d) => ({ id: d.id, label: d.labelFr }));

  // Get provinces list
  const provincesList = provinces.length > 0 ? provinces.map((p) => p.nameFr) : FALLBACK_PROVINCES;

  // State for show/hide password in credentials modal
  const [showTempPassword, setShowTempPassword] = useState(false);

  if (!isOpen) return null;

  // Credentials modal - shown after creating student without email
  if (showCredentials && createdCredentials) {
    return ReactDOM.createPortal(
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl w-full max-w-md overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-white">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Key className="text-green-600" size={24} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Identifiants créés</h2>
                <p className="text-sm text-gray-500">{createdCredentials.studentName}</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Success message */}
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg mb-4">
              <CheckCircle className="text-green-600" size={20} />
              <span className="text-sm text-green-800">Compte étudiant créé avec succès</span>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              L'étudiant n'a pas d'email. Voici ses identifiants de connexion à partager:
            </p>

            {/* Username field */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Identifiant</label>
              <div className="flex items-center gap-2">
                <div className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg font-mono">
                  {createdCredentials.username}
                </div>
                <button
                  onClick={() => copyToClipboard(createdCredentials.username, 'username')}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Copier l'identifiant"
                >
                  {copiedField === 'username' ? (
                    <Check className="text-green-600" size={20} />
                  ) : (
                    <Copy size={20} />
                  )}
                </button>
              </div>
            </div>

            {/* Password field */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe temporaire</label>
              <div className="flex items-center gap-2">
                <div className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg font-mono">
                  {showTempPassword ? createdCredentials.tempPassword : '••••••••••••'}
                </div>
                <button
                  onClick={() => setShowTempPassword(!showTempPassword)}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  title={showTempPassword ? 'Masquer' : 'Afficher'}
                >
                  {showTempPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
                <button
                  onClick={() => copyToClipboard(createdCredentials.tempPassword, 'password')}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Copier le mot de passe"
                >
                  {copiedField === 'password' ? (
                    <Check className="text-green-600" size={20} />
                  ) : (
                    <Copy size={20} />
                  )}
                </button>
              </div>
            </div>

            {/* Note */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-6">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> L'étudiant devra changer son mot de passe lors de sa première connexion.
              </p>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-3 mb-4">
              <button
                onClick={printCredentials}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Printer size={18} />
                Imprimer
              </button>
              <button
                onClick={downloadCredentials}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Download size={18} />
                Télécharger
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end p-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleCloseCredentials}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Terminé
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <GraduationCap className="text-green-600" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Ajouter un étudiant</h2>
              <p className="text-sm text-gray-500">Créer un nouveau compte étudiant avec toutes les informations</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onBulkImport && (
              <button
                onClick={() => {
                  handleClose();
                  onBulkImport();
                }}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors shadow-sm"
                title="Importer plusieurs étudiants depuis Excel"
              >
                <FileSpreadsheet size={18} />
                <span>Import en masse</span>
              </button>
            )}
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Success Message */}
          {success && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
              <Check className="text-green-600" size={20} />
              <span className="text-green-800">Étudiant créé avec succès !</span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
              <AlertCircle className="text-red-600" size={20} />
              <span className="text-red-800">{error}</span>
            </div>
          )}

          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-medium">Création de compte étudiant</p>
              <p>Remplissez les champs obligatoires (*). L'étudiant pourra compléter son profil après activation.</p>
            </div>
          </div>

          {/* Academic Year Selection */}
          {academicYears.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center gap-2">
                  <BookOpen size={16} className="text-primary-600" />
                  Année académique
                </div>
              </label>
              <select
                value={selectedAcademicYearId}
                onChange={(e) => setSelectedAcademicYearId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                {academicYears.map((year) => (
                  <option key={year.id} value={year.id}>
                    {year.name} {year.isCurrent ? '(Année en cours)' : ''}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                L&apos;année académique sera automatiquement mise à jour pour les étudiants non diplômés.
              </p>
            </div>
          )}

          {/* Section 1: Identité */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <SectionHeader
              title="Section 1: Identité"
              icon={<User size={18} className="text-primary-600" />}
              expanded={expandedSections.has('basic')}
              onToggle={() => toggleSection('basic')}
              required
            />

            {expandedSections.has('basic') && (
              <div className="p-6 space-y-4">
                {/* Nom / Prénom */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom <span className="text-red-500">*</span>
                    </label>
                    <input
                      ref={fieldRefs.nom}
                      type="text"
                      value={nom}
                      onChange={(e) => {
                        setNom(e.target.value.toUpperCase());
                        if (fieldErrors.nom) setFieldErrors(prev => ({ ...prev, nom: '' }));
                      }}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 uppercase ${
                        fieldErrors.nom ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                      placeholder="NOM"
                      required
                    />
                    {fieldErrors.nom && <p className="text-xs text-red-500 mt-1">{fieldErrors.nom}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prénom <span className="text-red-500">*</span>
                    </label>
                    <input
                      ref={fieldRefs.prenom}
                      type="text"
                      value={prenom}
                      onChange={(e) => {
                        setPrenom(capitalizeFirstLetter(e.target.value));
                        if (fieldErrors.prenom) setFieldErrors(prev => ({ ...prev, prenom: '' }));
                      }}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                        fieldErrors.prenom ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                      placeholder="Prénom"
                      required
                    />
                    {fieldErrors.prenom && <p className="text-xs text-red-500 mt-1">{fieldErrors.prenom}</p>}
                  </div>
                </div>

                {/* Nom / Prénom Arabe */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">الاسم العائلي / Nom en arabe</label>
                    <ArabicInput
                      value={nomArabe}
                      onChange={setNomArabe}
                      placeholder="الاسم العائلي"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">الاسم الشخصي / Prénom en arabe</label>
                    <ArabicInput
                      value={prenomArabe}
                      onChange={setPrenomArabe}
                      placeholder="الاسم الشخصي"
                    />
                  </div>
                </div>

                {/* CNIE / Passport */}
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-2">
                  <p className="text-sm text-amber-800 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    <span><strong>Document d'identité requis:</strong> Remplissez soit la CNIE (marocains) soit le Passeport (étrangers). Au moins un des deux est obligatoire.</span>
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      N° CNIE <span className="text-gray-500 text-xs">(marocains)</span>
                    </label>
                    <input
                      type="text"
                      value={cnie}
                      onChange={(e) => setCnie(e.target.value.toUpperCase())}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="AB123456"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      N° Passeport <span className="text-gray-500 text-xs">(étrangers)</span>
                    </label>
                    <input
                      type="text"
                      value={passport}
                      onChange={(e) => setPassport(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="Numéro passeport"
                    />
                  </div>
                </div>

                {/* Sexe / Date de naissance */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sexe</label>
                    <div className="flex gap-4">
                      <label
                        className={`flex-1 flex items-center justify-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                          sexe === 'M' ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <input
                          type="radio"
                          value="M"
                          checked={sexe === 'M'}
                          onChange={() => setSexe('M')}
                          className="sr-only"
                        />
                        <span>Masculin</span>
                      </label>
                      <label
                        className={`flex-1 flex items-center justify-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                          sexe === 'F' ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <input
                          type="radio"
                          value="F"
                          checked={sexe === 'F'}
                          onChange={() => setSexe('F')}
                          className="sr-only"
                        />
                        <span>Féminin</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date de naissance</label>
                    <input
                      type="date"
                      value={dateNaissance}
                      onChange={(e) => setDateNaissance(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      style={{ colorScheme: 'light' }}
                    />
                  </div>
                </div>

                {/* Pays d'origine / Handicap */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pays d'origine</label>
                    <FilterableDropdown
                      options={countries.map((c) => ({
                        value: c.code,
                        label: c.nameFr,
                      }))}
                      value={paysOrigine}
                      onChange={setPaysOrigine}
                      placeholder="Sélectionner un pays..."
                    />
                  </div>
                  <div ref={fieldRefs.handicap}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Situation de handicap
                    </label>
                    <select
                      value={handicap}
                      onChange={(e) => {
                        setHandicap(e.target.value);
                        if (fieldErrors.handicap) setFieldErrors(prev => ({ ...prev, handicap: '' }));
                      }}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                        fieldErrors.handicap ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Sélectionner...</option>
                      {HANDICAP_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    {fieldErrors.handicap && <p className="text-xs text-red-500 mt-1">{fieldErrors.handicap}</p>}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Section 2: Coordonnées */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <SectionHeader
              title="Section 2: Coordonnées"
              icon={<Phone size={18} className="text-primary-600" />}
              expanded={expandedSections.has('coordinates')}
              onToggle={() => toggleSection('coordinates')}
              required
            />

            {expandedSections.has('coordinates') && (
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-gray-400 text-xs">(optionnel)</span>
                  </label>
                  <div className="relative">
                    <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${fieldErrors.email ? 'text-red-400' : 'text-gray-400'}`} />
                    <input
                      ref={fieldRefs.email}
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (fieldErrors.email) setFieldErrors(prev => ({ ...prev, email: '' }));
                      }}
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                        fieldErrors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                      placeholder="email@institution.ma"
                    />
                  </div>
                  {!email && (
                    <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                      <Info size={12} />
                      Sans email, l'étudiant utilisera son code étudiant comme identifiant
                    </p>
                  )}
                  {fieldErrors.email && <p className="text-xs text-red-500 mt-1">{fieldErrors.email}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Numéro de téléphone <span className="text-red-500">*</span>
                  </label>
                  <div className={`flex ${fieldErrors.telephone ? 'ring-1 ring-red-500 rounded-lg' : ''}`}>
                    <PhoneCountrySelector value={phoneCountry} onChange={setPhoneCountry} />
                    <input
                      ref={fieldRefs.telephone}
                      type="tel"
                      value={telephone}
                      onChange={(e) => {
                        setTelephone(e.target.value);
                        if (fieldErrors.telephone) setFieldErrors(prev => ({ ...prev, telephone: '' }));
                      }}
                      className={`flex-1 px-4 py-2 border border-l-0 rounded-r-lg focus:ring-2 focus:ring-primary-500 ${
                        fieldErrors.telephone ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                      placeholder="6XX XXX XXX"
                      required
                    />
                  </div>
                  {fieldErrors.telephone && <p className="text-xs text-red-500 mt-1">{fieldErrors.telephone}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                  <textarea
                    value={adresse}
                    onChange={(e) => setAdresse(e.target.value)}
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="Adresse complète"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Section 3: Contact d'Urgence */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <SectionHeader
              title="Section 3: Contact d'Urgence"
              icon={<AlertTriangle size={18} className="text-amber-500" />}
              expanded={expandedSections.has('emergency')}
              onToggle={() => toggleSection('emergency')}
              optional
            />

            {expandedSections.has('emergency') && (
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom du contact</label>
                    <input
                      type="text"
                      value={urgenceNom}
                      onChange={(e) => setUrgenceNom(capitalizeFirstLetter(e.target.value))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="Nom complet"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Relation</label>
                    <select
                      value={urgenceRelation}
                      onChange={(e) => setUrgenceRelation(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      {emergencyRelationSelectOptions().map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Numéro de téléphone</label>
                  <div className="flex">
                    <PhoneCountrySelector value={urgencePhoneCountry} onChange={setUrgencePhoneCountry} />
                    <input
                      type="tel"
                      value={urgenceNumero}
                      onChange={(e) => setUrgenceNumero(e.target.value)}
                      className="flex-1 px-4 py-2 border border-l-0 border-gray-300 rounded-r-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="6XX XXX XXX"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Adresse du contact</label>
                  <input
                    type="text"
                    value={urgenceAdresse}
                    onChange={(e) => setUrgenceAdresse(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="Adresse complète"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Section 4: Inscription Académique */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <SectionHeader
              title="Section 4: Inscription Académique"
              icon={<GraduationCap size={18} className="text-primary-600" />}
              expanded={expandedSections.has('academic')}
              onToggle={() => toggleSection('academic')}
              required
            />

            {expandedSections.has('academic') && (
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">N° Massar/CNE</label>
                    <input
                      type="text"
                      value={numeroMassarCNE}
                      onChange={(e) => setNumeroMassarCNE(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="R123456789 ou 'be'"
                    />
                    <p className="text-xs text-gray-500 mt-1">'be' pour les étudiants ayant obtenu un baccalauréat non marocain</p>
                  </div>
                  <div ref={fieldRefs.filiere}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Filière <span className="text-red-500">*</span>
                    </label>
                    <div className={fieldErrors.filiere ? 'ring-1 ring-red-500 rounded-lg' : ''}>
                      <FilterableDropdown
                        options={allFilieres.map((f) => ({
                          value: f.codeFiliere,
                          label: f.filiere,
                          sublabel: `${f.diplome} - ${f.nombreAnnees} ans`,
                        }))}
                        value={codeFiliereInscrit}
                        onChange={(value: string) => {
                          setCodeFiliereInscrit(value);
                          // Reset niveau to 1 when filière changes
                          setNiveauFormation(1);
                          if (fieldErrors.filiere) setFieldErrors(prev => ({ ...prev, filiere: '' }));
                        }}
                        placeholder="Sélectionner une filière..."
                        error={!!fieldErrors.filiere}
                      />
                    </div>
                    {fieldErrors.filiere && <p className="text-xs text-red-500 mt-1">{fieldErrors.filiere}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Niveau d'inscription <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={niveauFormation}
                      onChange={(e) => {
                        const newNiveau = parseInt(e.target.value);
                        setNiveauFormation(newNiveau);
                      }}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                        !codeFiliereInscrit
                          ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                          : 'border-gray-300'
                      }`}
                      disabled={!codeFiliereInscrit}
                      required
                    >
                      {!codeFiliereInscrit ? (
                        <option value="">Sélectionnez d'abord une filière</option>
                      ) : (
                        availableNiveaux.map((level) => (
                          <option key={level} value={level}>
                            {level === 1 ? '1ère année' : `${level}ème année`}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Situation</label>
                    <select
                      value={situation}
                      onChange={(e) => setSituation(e.target.value as 'N' | 'R' | 'P')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      {SITUATION_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {selectedFiliere && (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Diplôme:</strong> {selectedFiliere.diplome} | <strong>Durée:</strong> {selectedFiliere.nombreAnnees} ans
                      {selectedFiliere.isOption && <span className="ml-2 text-purple-600">(Spécialisation)</span>}
                    </p>
                  </div>
                )}

                {/* Type de Formation */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Type de formation <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setTypeFormation('INITIALE')}
                      className={`px-4 py-3 text-sm font-medium rounded-lg border-2 transition-all ${
                        typeFormation === 'INITIALE'
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded-full border-2 ${
                          typeFormation === 'INITIALE'
                            ? 'border-blue-500 bg-blue-500'
                            : 'border-gray-400'
                        }`}>
                          {typeFormation === 'INITIALE' && (
                            <div className="w-full h-full rounded-full bg-white scale-50"></div>
                          )}
                        </div>
                        <span>Formation Initiale</span>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setTypeFormation('CONTINUE')}
                      className={`px-4 py-3 text-sm font-medium rounded-lg border-2 transition-all ${
                        typeFormation === 'CONTINUE'
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded-full border-2 ${
                          typeFormation === 'CONTINUE'
                            ? 'border-blue-500 bg-blue-500'
                            : 'border-gray-400'
                        }`}>
                          {typeFormation === 'CONTINUE' && (
                            <div className="w-full h-full rounded-full bg-white scale-50"></div>
                          )}
                        </div>
                        <span>Formation Continue</span>
                      </div>
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">
                    La plupart des étudiants sont en Formation Initiale
                  </p>
                </div>

                {/* Campus Selection - Only show if multiple campuses exist */}
                {campuses.length > 1 && (
                  <div ref={fieldRefs.campus}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Campus d'affectation <span className="text-red-500">*</span>
                    </label>
                    <div className={fieldErrors.campus ? 'ring-1 ring-red-500 rounded-lg' : ''}>
                      <FilterableDropdown
                        options={campuses.map((c) => ({
                          value: c.id,
                          label: c.name,
                          sublabel: c.city + (c.isMainCampus ? ' (Campus principal)' : ''),
                        }))}
                        value={campusId}
                        onChange={(value: string) => {
                          setCampusId(value);
                          if (fieldErrors.campus) setFieldErrors(prev => ({ ...prev, campus: '' }));
                        }}
                        placeholder="Sélectionner un campus..."
                        error={requiresCampusSelection && !campusId || !!fieldErrors.campus}
                      />
                    </div>
                    {fieldErrors.campus && <p className="text-xs text-red-500 mt-1">{fieldErrors.campus}</p>}
                    <p className="text-xs text-gray-500 mt-1">
                      L'étudiant sera affilié à ce campus
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">1ère année d'inscription</label>
                  <select
                    value={premiereAnneeInscription}
                    onChange={(e) => setPremiereAnneeInscription(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    {anneesUniversitaires.map((a) => (
                      <option key={a} value={a}>
                        {a}
                      </option>
                    ))}
                  </select>
                </div>

                {/* E-DEP Checkbox */}
                <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <input
                    type="checkbox"
                    id="includeInEdep"
                    checked={includeInEdep}
                    onChange={(e) => setIncludeInEdep(e.target.checked)}
                    className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="includeInEdep" className="text-sm text-amber-800">
                    <strong>Inclure dans la liste E-DEP</strong>
                    <p className="text-xs text-amber-700 mt-0.5">
                      L'étudiant sera envoyé au Ministère de l'Enseignement Supérieur
                    </p>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Section 5: Frais Financiers */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <SectionHeader
              title="Section 5: Frais Financiers"
              icon={<CreditCard size={18} className="text-primary-600" />}
              expanded={expandedSections.has('financial')}
              onToggle={() => toggleSection('financial')}
              optional
            />

            {expandedSections.has('financial') && (
              <div className="p-6 space-y-6">
                {loadingFinancialOptions ? (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Chargement des options financières...</span>
                  </div>
                ) : (
                  <>
                    {/* Tuition Preview (if filière and niveau selected) */}
                    {tuitionPreview?.isConfigured && (
                      <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                        <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                          <CreditCard className="w-4 h-4" />
                          Aperçu des frais de base
                        </h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-700">Frais d'inscription:</span>
                            <span className="font-semibold">{tuitionPreview.registrationFee.toLocaleString()} MAD</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-700">Frais de scolarité:</span>
                            <span className="font-semibold">{tuitionPreview.tuitionAmount.toLocaleString()} MAD</span>
                          </div>
                          <div className="flex justify-between pt-2 border-t border-blue-200">
                            <span className="text-blue-800 font-medium">Sous-total base:</span>
                            <span className="font-bold text-blue-900">{tuitionPreview.total.toLocaleString()} MAD</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {!tuitionPreview?.isConfigured && codeFiliereInscrit && niveauFormation && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                        <AlertCircle className="w-4 h-4 inline mr-2" />
                        Aucune grille tarifaire configurée pour cette filière/niveau. Les frais de base seront configurés ultérieurement.
                      </div>
                    )}

                    {/* Ancillary Fees Selection */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <Plus className="w-4 h-4 text-blue-600" />
                        Frais annexes
                        {allAncillaryFees.length > 0 && (
                          <span className="text-xs text-gray-500 font-normal">
                            ({allAncillaryFees.length} disponible{allAncillaryFees.length > 1 ? 's' : ''})
                          </span>
                        )}
                      </h4>

                      {allAncillaryFees.length === 0 ? (
                        <p className="text-sm text-gray-500 italic">Aucun frais annexe disponible</p>
                      ) : (
                        <div className="space-y-2">
                          {allAncillaryFees.filter(f => f.isActive).map((fee) => (
                            <label
                              key={fee.id}
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <input
                                  type="checkbox"
                                  checked={selectedAncillaryFeeIds.includes(fee.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedAncillaryFeeIds([...selectedAncillaryFeeIds, fee.id]);
                                    } else {
                                      setSelectedAncillaryFeeIds(selectedAncillaryFeeIds.filter(id => id !== fee.id));
                                    }
                                  }}
                                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <div>
                                  <span className="text-sm font-medium text-gray-900">{fee.name}</span>
                                  <span className="text-xs text-gray-500 ml-2">({fee.code})</span>
                                  {fee.description && (
                                    <p className="text-xs text-gray-500 mt-0.5">{fee.description}</p>
                                  )}
                                </div>
                              </div>
                              <span className="text-sm font-semibold text-gray-900">
                                {fee.amount.toLocaleString()} MAD
                              </span>
                            </label>
                          ))}
                        </div>
                      )}

                      {selectedAncillaryFeesTotal > 0 && (
                        <div className="mt-3 p-2 bg-blue-100 rounded flex justify-between items-center">
                          <span className="text-sm text-blue-800">Total frais annexes sélectionnés:</span>
                          <span className="font-bold text-blue-900">+{selectedAncillaryFeesTotal.toLocaleString()} MAD</span>
                        </div>
                      )}
                    </div>

                    {/* Discounts Selection */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <Gift className="w-4 h-4 text-green-600" />
                        Remises / Bourses
                        <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          À valider
                        </span>
                        {allDiscounts.length > 0 && (
                          <span className="text-xs text-gray-500 font-normal">
                            ({allDiscounts.length} disponible{allDiscounts.length > 1 ? 's' : ''})
                          </span>
                        )}
                      </h4>

                      {allDiscounts.length === 0 ? (
                        <p className="text-sm text-gray-500 italic">Aucune remise disponible</p>
                      ) : (
                        <div className="space-y-2">
                          {allDiscounts.filter(d => d.isActive).map((discount) => {
                            const discountInfo = selectedDiscountsInfo.find(d => d.id === discount.id);
                            return (
                              <label
                                key={discount.id}
                                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-green-50 hover:border-green-300 transition-colors"
                              >
                                <div className="flex items-center gap-3">
                                  <input
                                    type="checkbox"
                                    checked={selectedDiscountIds.includes(discount.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedDiscountIds([...selectedDiscountIds, discount.id]);
                                      } else {
                                        setSelectedDiscountIds(selectedDiscountIds.filter(id => id !== discount.id));
                                      }
                                    }}
                                    className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                  />
                                  <div>
                                    <span className="text-sm font-medium text-gray-900">{discount.name}</span>
                                    <span className="text-xs text-gray-500 ml-2">({discount.code})</span>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                      {discount.percentageValue !== null
                                        ? `${discount.percentageValue}% de réduction${discount.maxDeductionAmount ? ` (max ${discount.maxDeductionAmount.toLocaleString()} MAD)` : ''}`
                                        : `${discount.fixedAmount?.toLocaleString()} MAD de réduction`}
                                      {discount.requiresApproval && ' • Validation requise'}
                                    </p>
                                  </div>
                                </div>
                                {discountInfo && (
                                  <span className="text-sm font-semibold text-green-700">
                                    -{discountInfo.calculatedAmount.toLocaleString()} MAD
                                  </span>
                                )}
                              </label>
                            );
                          })}
                        </div>
                      )}

                      {selectedDiscountsTotal > 0 && (
                        <div className="mt-3 p-2 bg-green-100 rounded flex justify-between items-center">
                          <span className="text-sm text-green-800 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Remises en attente de validation:
                          </span>
                          <span className="font-bold text-green-900">-{selectedDiscountsTotal.toLocaleString()} MAD</span>
                        </div>
                      )}
                    </div>

                    {/* Payment Schedule Selection */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-purple-600" />
                        Échéancier de paiement
                        {allPaymentSchedules.length > 0 && (
                          <span className="text-xs text-gray-500 font-normal">
                            ({allPaymentSchedules.filter(s => s.isActive).length} disponible{allPaymentSchedules.filter(s => s.isActive).length > 1 ? 's' : ''})
                          </span>
                        )}
                      </h4>

                      {allPaymentSchedules.length === 0 ? (
                        <p className="text-sm text-gray-500 italic">Aucun échéancier de paiement configuré</p>
                      ) : (
                        <div className="space-y-2">
                          {allPaymentSchedules.filter(s => s.isActive).map((schedule) => (
                            <label
                              key={schedule.id}
                              className={`flex items-start justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                                selectedPaymentScheduleId === schedule.id
                                  ? 'bg-purple-50 border-purple-300 ring-2 ring-purple-200'
                                  : 'bg-gray-50 border-gray-200 hover:bg-purple-50 hover:border-purple-200'
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                <input
                                  type="radio"
                                  name="paymentSchedule"
                                  checked={selectedPaymentScheduleId === schedule.id}
                                  onChange={() => setSelectedPaymentScheduleId(schedule.id)}
                                  className="mt-1 h-4 w-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                                />
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-gray-900">{schedule.name}</span>
                                    {schedule.isDefault && (
                                      <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                                        Par défaut
                                      </span>
                                    )}
                                  </div>
                                  {schedule.description && (
                                    <p className="text-xs text-gray-500 mt-0.5">{schedule.description}</p>
                                  )}
                                  <p className="text-xs text-gray-600 mt-1">
                                    {schedule.installments?.length || 0} échéance{(schedule.installments?.length || 0) > 1 ? 's' : ''}
                                    {schedule.installments && schedule.installments.length > 0 && (
                                      <span className="text-gray-400 ml-1">
                                        ({schedule.installments.map(i => `${i.percentageOfTotal}%`).join(' / ')})
                                      </span>
                                    )}
                                  </p>
                                </div>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Total Summary */}
                    {(tuitionPreview?.isConfigured || selectedAncillaryFeesTotal > 0 || selectedDiscountsTotal > 0) && (
                      <div className="mt-4 p-4 bg-gradient-to-r from-indigo-100 to-purple-100 border border-indigo-200 rounded-lg">
                        <h4 className="font-semibold text-indigo-900 mb-3">Récapitulatif</h4>
                        <div className="space-y-2 text-sm">
                          {tuitionPreview?.isConfigured && (
                            <div className="flex justify-between">
                              <span className="text-gray-700">Frais de base:</span>
                              <span className="font-medium">{tuitionPreview.total.toLocaleString()} MAD</span>
                            </div>
                          )}
                          {selectedAncillaryFeesTotal > 0 && (
                            <div className="flex justify-between text-blue-700">
                              <span>Frais annexes:</span>
                              <span className="font-medium">+{selectedAncillaryFeesTotal.toLocaleString()} MAD</span>
                            </div>
                          )}
                          <div className="flex justify-between pt-2 border-t border-indigo-300">
                            <span className="text-indigo-900 font-semibold">Total à payer:</span>
                            <span className="font-bold text-indigo-900 text-lg">{finalTotal.toLocaleString()} MAD</span>
                          </div>
                          {selectedDiscountsTotal > 0 && (
                            <div className="flex justify-between text-green-700 pt-1">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Remises (après validation):
                              </span>
                              <span className="font-medium">-{selectedDiscountsTotal.toLocaleString()} MAD</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Section 6: Diplôme d'Accès */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <SectionHeader
              title="Section 6: Diplôme d'Accès"
              icon={<Award size={18} className="text-primary-600" />}
              expanded={expandedSections.has('diploma')}
              onToggle={() => toggleSection('diploma')}
              optional
            />

            {expandedSections.has('diploma') && (
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Série du baccalauréat</label>
                    <FilterableDropdown
                      options={seriesBacList.map((s) => {
                        const isEtranger = s.type?.toLowerCase().includes('etranger') || s.type?.toLowerCase().includes('étranger');
                        return {
                          value: s.code,
                          label: `${isEtranger ? 'Étranger' : 'Marocain'} - ${s.label}`,
                          sublabel: s.code,
                        };
                      })}
                      value={serieBaccalaureat}
                      onChange={setSerieBaccalaureat}
                      placeholder="Sélectionner ou taper pour filtrer..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Année d'obtention du bac</label>
                    <select
                      value={anneeObtentionBac}
                      onChange={(e) => setAnneeObtentionBac(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Sélectionner...</option>
                      {anneesUniversitaires.map((a) => (
                        <option key={a} value={a}>
                          {a}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Diplôme d'accès</label>
                    <FilterableDropdown
                      options={diplomesList.map((d) => ({
                        value: d.label,
                        label: d.label,
                      }))}
                      value={diplomeAcces}
                      onChange={setDiplomeAcces}
                      placeholder="Sélectionner ou taper pour filtrer..."
                    />
                  </div>
                  {!isBacDiplome && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Spécialité du diplôme d'accès</label>
                      <input
                        type="text"
                        value={specialiteDiplomeAcces}
                        onChange={(e) => setSpecialiteDiplomeAcces(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        placeholder="Ex: Gestion, Informatique..."
                      />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mention du diplôme d'accès</label>
                    <select
                      value={mentionDiplomeAcces}
                      onChange={(e) => setMentionDiplomeAcces(e.target.value as 'p' | 'ab' | 'b' | 'tb' | 'e' | 'ns')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      {MENTION_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Lieu d'obtention</label>
                    <FilterableDropdown
                      options={provincesList.map((p) => ({
                        value: p,
                        label: p,
                      }))}
                      value={lieuObtentionDiplomeAcces}
                      onChange={setLieuObtentionDiplomeAcces}
                      placeholder="Sélectionner ou taper pour filtrer..."
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Section 7: Statut & Aides (optional for staff creating students) */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <SectionHeader
              title="Section 7: Statut & Aides"
              icon={<Briefcase size={18} className="text-primary-600" />}
              expanded={expandedSections.has('status')}
              onToggle={() => toggleSection('status')}
            />

            {expandedSections.has('status') && (
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div ref={fieldRefs.resident}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Résident (hébergement universitaire)
                    </label>
                    <select
                      value={resident}
                      onChange={(e) => {
                        setResident(e.target.value as 'O' | 'N');
                        if (fieldErrors.resident) setFieldErrors(prev => ({ ...prev, resident: '' }));
                      }}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                        fieldErrors.resident ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    >
                      {OUI_NON_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    {fieldErrors.resident && <p className="text-xs text-red-500 mt-1">{fieldErrors.resident}</p>}
                  </div>
                  <div ref={fieldRefs.fonctionnaire}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fonctionnaire
                    </label>
                    <select
                      value={fonctionnaire}
                      onChange={(e) => {
                        setFonctionnaire(e.target.value as 'O' | 'N');
                        if (fieldErrors.fonctionnaire) setFieldErrors(prev => ({ ...prev, fonctionnaire: '' }));
                      }}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                        fieldErrors.fonctionnaire ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    >
                      {OUI_NON_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    {fieldErrors.fonctionnaire && <p className="text-xs text-red-500 mt-1">{fieldErrors.fonctionnaire}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div ref={fieldRefs.pourcentageBourse}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Pourcentage de la bourse
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={pourcentageBourse}
                        onChange={(e) => {
                          setPourcentageBourse(parseInt(e.target.value) || 0);
                          if (fieldErrors.pourcentageBourse) setFieldErrors(prev => ({ ...prev, pourcentageBourse: '' }));
                        }}
                        className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                          fieldErrors.pourcentageBourse ? 'border-red-500 bg-red-50' : 'border-gray-300'
                        }`}
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">0% si pas de bourse, 100% si bourse complète</p>
                    {fieldErrors.pourcentageBourse && <p className="text-xs text-red-500 mt-1">{fieldErrors.pourcentageBourse}</p>}
                  </div>
                  <div ref={fieldRefs.mobilite}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mobilité (échange)
                    </label>
                    <select
                      value={mobilite}
                      onChange={(e) => {
                        setMobilite(e.target.value as 'O' | 'N');
                        if (fieldErrors.mobilite) setFieldErrors(prev => ({ ...prev, mobilite: '' }));
                      }}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                        fieldErrors.mobilite ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    >
                      {OUI_NON_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    {fieldErrors.mobilite && <p className="text-xs text-red-500 mt-1">{fieldErrors.mobilite}</p>}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Section 8: Documents */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <SectionHeader
              title="Section 8: Documents"
              icon={<FileText size={18} className="text-primary-600" />}
              expanded={expandedSections.has('documents')}
              onToggle={() => toggleSection('documents')}
              optional
            />

            {expandedSections.has('documents') && (
              <div className="p-6 space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex gap-2">
                  <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-blue-700">
                    Les documents peuvent être ajoutés maintenant ou par l'étudiant après activation de son compte. Les documents sont automatiquement enregistrés sur le serveur.
                  </p>
                </div>

                {/* ID Document Type Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type de document d'identité</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setIdDocumentType('CIN')}
                      className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
                        idDocumentType === 'CIN'
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <CreditCard className="w-5 h-5" />
                      <span className="font-medium">CIN Marocaine</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setIdDocumentType('PASSPORT')}
                      className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
                        idDocumentType === 'PASSPORT'
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <BookOpen className="w-5 h-5" />
                      <span className="font-medium">Passeport</span>
                    </button>
                  </div>
                </div>

                {/* Document List */}
                <div className="bg-gray-50 rounded-lg px-4">
                  {idDocumentType === 'CIN' ? (
                    <>
                      <DocumentRow
                        label="Scan CIN - Recto"
                        required={false}
                        document={getDocument('CIN_FRONT')}
                        onUpload={(f) => handleUpload('CIN_FRONT', f)}
                        onRemove={() => handleRemove('CIN_FRONT')}
                        onPreview={() => setPreviewDocument(getDocument('CIN_FRONT') || null)}
                      />
                      <DocumentRow
                        label="Scan CIN - Verso"
                        required={false}
                        document={getDocument('CIN_BACK')}
                        onUpload={(f) => handleUpload('CIN_BACK', f)}
                        onRemove={() => handleRemove('CIN_BACK')}
                        onPreview={() => setPreviewDocument(getDocument('CIN_BACK') || null)}
                      />
                    </>
                  ) : (
                    <DocumentRow
                      label="Scan Passeport"
                      required={false}
                      document={getDocument('PASSPORT')}
                      onUpload={(f) => handleUpload('PASSPORT', f)}
                      onRemove={() => handleRemove('PASSPORT')}
                      onPreview={() => setPreviewDocument(getDocument('PASSPORT') || null)}
                    />
                  )}
                  <DocumentRow
                    label="Diplôme du Baccalauréat (scan)"
                    required={false}
                    document={getDocument('DIPLOME_BAC_SCAN')}
                    onUpload={(f) => handleUpload('DIPLOME_BAC_SCAN', f)}
                    onRemove={() => handleRemove('DIPLOME_BAC_SCAN')}
                    onPreview={() => setPreviewDocument(getDocument('DIPLOME_BAC_SCAN') || null)}
                  />
                  <DocumentRow
                    label="Relevé de notes du BAC"
                    required={false}
                    document={getDocument('RELEVES_NOTES_BAC')}
                    onUpload={(f) => handleUpload('RELEVES_NOTES_BAC', f)}
                    onRemove={() => handleRemove('RELEVES_NOTES_BAC')}
                    onPreview={() => setPreviewDocument(getDocument('RELEVES_NOTES_BAC') || null)}
                  />
                  {!isBacDiplome && (
                    <>
                      <DocumentRow
                        label={`Diplôme d'accès: ${diplomeAcces}`}
                        required={false}
                        document={getDocument('DIPLOME_ACCES_SCAN')}
                        onUpload={(f) => handleUpload('DIPLOME_ACCES_SCAN', f)}
                        onRemove={() => handleRemove('DIPLOME_ACCES_SCAN')}
                        onPreview={() => setPreviewDocument(getDocument('DIPLOME_ACCES_SCAN') || null)}
                      />
                      <DocumentRow
                        label={`Relevé de notes: ${diplomeAcces}`}
                        required={false}
                        document={getDocument('RELEVES_NOTES')}
                        onUpload={(f) => handleUpload('RELEVES_NOTES', f)}
                        onRemove={() => handleRemove('RELEVES_NOTES')}
                        onPreview={() => setPreviewDocument(getDocument('RELEVES_NOTES') || null)}
                      />
                    </>
                  )}
                  <DocumentRow
                    label="CV"
                    required={false}
                    document={getDocument('CV')}
                    onUpload={(f) => handleUpload('CV', f)}
                    onRemove={() => handleRemove('CV')}
                    onPreview={() => setPreviewDocument(getDocument('CV') || null)}
                  />
                  <DocumentRow
                    label="Lettre de motivation"
                    required={false}
                    document={getDocument('LETTRE_MOTIVATION')}
                    onUpload={(f) => handleUpload('LETTRE_MOTIVATION', f)}
                    onRemove={() => handleRemove('LETTRE_MOTIVATION')}
                    onPreview={() => setPreviewDocument(getDocument('LETTRE_MOTIVATION') || null)}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Section 9: Physical Documents Tracking */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <SectionHeader
              title="Section 9: Suivi Documents Physiques"
              icon={<ClipboardCheck size={18} className="text-primary-600" />}
              expanded={expandedSections.has('physicalDocs')}
              onToggle={() => toggleSection('physicalDocs')}
              optional
            />

            {expandedSections.has('physicalDocs') && (
              <div className="p-6 space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex gap-2">
                  <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-blue-700">
                    Cochez les documents physiques (papier) que vous avez recu de cet etudiant. Cette section permet de suivre la reception des dossiers physiques.
                  </p>
                </div>

                {/* Required Documents */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-red-500"></span>
                    Documents obligatoires
                  </h4>

                  <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={physicalDocStudentCin}
                      onChange={(e) => setPhysicalDocStudentCin(e.target.checked)}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Copie CIN / Passeport</span>
                  </label>

                  <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={physicalDocStudentBac}
                      onChange={(e) => setPhysicalDocStudentBac(e.target.checked)}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Attestation du Baccalaureat</span>
                  </label>

                  <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={physicalDocStudentReleveBac}
                      onChange={(e) => setPhysicalDocStudentReleveBac(e.target.checked)}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Releve de notes du BAC</span>
                  </label>
                </div>

                {/* Conditional Documents */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                    Documents conditionnels
                  </h4>

                  {!isBacDiplome && (
                    <>
                      <label className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg hover:bg-yellow-100 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={physicalDocStudentDernierDiplome}
                          onChange={(e) => setPhysicalDocStudentDernierDiplome(e.target.checked)}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <div>
                          <span className="text-sm text-gray-700">Dernier diplome obtenu</span>
                          <p className="text-xs text-yellow-700">Requis car diplome d'acces: {diplomeAcces}</p>
                        </div>
                      </label>

                      <label className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg hover:bg-yellow-100 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={physicalDocStudentReleveDiplome}
                          onChange={(e) => setPhysicalDocStudentReleveDiplome(e.target.checked)}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <div>
                          <span className="text-sm text-gray-700">Releve de notes du diplome d'acces</span>
                          <p className="text-xs text-yellow-700">Requis car diplome d'acces: {diplomeAcces}</p>
                        </div>
                      </label>
                    </>
                  )}

                  {niveauFormation > 1 && (
                    <label className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg hover:bg-yellow-100 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={physicalDocStudentReleveAnneesPrecedentes}
                        onChange={(e) => setPhysicalDocStudentReleveAnneesPrecedentes(e.target.checked)}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <div>
                        <span className="text-sm text-gray-700">Releves de notes des annees precedentes</span>
                        <p className="text-xs text-yellow-700">Requis car niveau de formation: {niveauFormation}</p>
                      </div>
                    </label>
                  )}

                  {isBacDiplome && niveauFormation === 1 && (
                    <p className="text-sm text-gray-500 italic p-3 bg-gray-50 rounded-lg">
                      Aucun document conditionnel requis (etudiant en 1ere annee avec BAC)
                    </p>
                  )}
                </div>

                {/* Optional Documents */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                    Documents optionnels
                  </h4>

                  <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={physicalDocStudentCv}
                      onChange={(e) => setPhysicalDocStudentCv(e.target.checked)}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">CV</span>
                  </label>

                  <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={physicalDocStudentLettreMotivation}
                      onChange={(e) => setPhysicalDocStudentLettreMotivation(e.target.checked)}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Lettre de motivation</span>
                  </label>
                </div>
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100"
            disabled={loading}
          >
            Annuler
          </button>
          <button
            type="submit"
            onClick={(e) => handleSubmit(e, false)}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            disabled={loading || success}
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Création...
              </>
            ) : success ? (
              <>
                <Check size={18} />
                Créé !
              </>
            ) : (
              "Créer l'étudiant"
            )}
          </button>
          {profileComplete && !loading && !success && (
            <button
              type="button"
              onClick={(e) => handleSubmit(e, true)}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              disabled={loading || success}
              title="Le profil est complet - l'étudiant sera créé avec un statut actif"
            >
              <CheckCircle size={18} />
              Créer et valider
            </button>
          )}
        </div>

        {/* Preview Modal */}
        {previewDocument && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70" onClick={() => setPreviewDocument(null)}>
            <div className="relative max-w-4xl max-h-[90vh] bg-white rounded-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => setPreviewDocument(null)}
                className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 z-10"
              >
                <X className="w-5 h-5" />
              </button>
              {previewDocument.previewUrl.toLowerCase().includes('.pdf') ? (
                <div className="w-[600px] h-[400px] flex flex-col items-center justify-center p-8 text-center">
                  <FileText className="w-16 h-16 text-red-500 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Document PDF</h3>
                  <p className="text-sm text-gray-600 mb-4">Les fichiers PDF s'ouvrent dans un nouvel onglet.</p>
                  <a
                    href={previewDocument.previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    onClick={() => setPreviewDocument(null)}
                  >
                    Ouvrir le PDF
                  </a>
                </div>
              ) : (
                <img src={previewDocument.previewUrl} alt="Document preview" className="max-w-full max-h-[90vh] object-contain" />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateStudentModal;
